/**
 * Zustand store for real-time market statistics.
 * 
 * Single subscription pattern (industry standard):
 * - ONE WebSocket listener receives stats for ALL markets
 * - Store holds the data, components read via selectors
 * - Only components subscribed to a changed market re-render
 * 
 * This replaces the old per-component subscription model where every
 * useMarketStats() call created its own WS listener + setInterval.
 * 
 * Usage:
 * ```tsx
 * // Single market stat (preferred for cells/rows):
 * const stat = useMarketStatFromStore("BTC-USD");
 * 
 * // All stats (for bulk operations):
 * const { stats, isLoading, isConnected } = useAllMarketStats();
 * ```
 */

import { useMemo } from "react";
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";
import type { WsMarketStatData } from "../ws-client";

/**
 * Parsed market stats info returned by selector hooks.
 * Kept identical to the old useMarketStat() return type for backwards compatibility.
 */
export interface MarketStatsInfo {
  marketPrice: number;
  oraclePrice: number;
  openInterestUsd: number;
  volume24hUsd: number;
  fundingRate: number;
  nextFundingTime: number; // timestamp in ms
}

interface MarketStatsState {
  /** Raw stats indexed by market symbol for O(1) lookup */
  stats: Record<string, WsMarketStatData>;
  /** Whether we've received initial data */
  isInitialized: boolean;
  /** Whether the stats WebSocket is connected */
  isConnected: boolean;

  /** Bulk update from WebSocket stats message. Only updates changed markets. */
  setStats: (stats: WsMarketStatData[]) => void;
  /** Set connection status */
  setConnected: (connected: boolean) => void;
  /** Clear all stats */
  clear: () => void;
}

export const useMarketStatsStore = create<MarketStatsState>()(
  subscribeWithSelector((set, get) => ({
    stats: {},
    isInitialized: false,
    isConnected: false,

    setStats: (statsList) => {
      const currentStats = get().stats;
      const newStats: Record<string, WsMarketStatData> = {};
      let hasChanges = false;

      for (const stat of statsList) {
        const existing = currentStats[stat.market];
        // Only flag as changed if key fields differ
        if (
          !existing ||
          existing.market_price !== stat.market_price ||
          existing.oracle_price !== stat.oracle_price ||
          existing.open_interest_usd !== stat.open_interest_usd ||
          existing.volume_24h_usd !== stat.volume_24h_usd ||
          existing.funding_rate !== stat.funding_rate ||
          existing.next_funding_at !== stat.next_funding_at ||
          existing.max_leverage !== stat.max_leverage
        ) {
          newStats[stat.market] = stat;
          hasChanges = true;
        } else {
          // Keep existing reference to avoid unnecessary selector triggers
          newStats[stat.market] = existing;
        }
      }

      if (hasChanges) {
        set({
          stats: newStats,
          isInitialized: true,
          isConnected: true,
        });
      }
    },

    setConnected: (connected) => set({ isConnected: connected }),

    clear: () => set({ stats: {}, isInitialized: false }),
  }))
);

// ---------------------------------------------------------------------------
// Selector hooks
// ---------------------------------------------------------------------------

/**
 * Get parsed stats for a single market.
 * Returns null if no data available for that market.
 * Uses useShallow to prevent re-renders when unrelated markets update.
 */
export const useMarketStatFromStore = (market: string): MarketStatsInfo | null => {
  return useMarketStatsStore(
    useShallow((state) => {
      const stat = state.stats[market];
      if (!stat) return null;
      return {
        marketPrice: parseFloat(stat.market_price),
        oraclePrice: parseFloat(stat.oracle_price),
        openInterestUsd: parseFloat(stat.open_interest_usd),
        volume24hUsd: parseFloat(stat.volume_24h_usd),
        fundingRate: parseFloat(stat.funding_rate),
        nextFundingTime: new Date(stat.next_funding_at).getTime(),
      };
    })
  );
};

/**
 * Get raw WsMarketStatData for a single market.
 * Useful for components that need unparsed fields like max_leverage.
 */
export const useMarketStatRaw = (market: string): WsMarketStatData | undefined => {
  return useMarketStatsStore((state) => state.stats[market]);
};

/**
 * Get all stats as an array (backwards compatible with old useMarketStats).
 * 
 * Selects the stats Record directly (stable reference from store) and converts
 * to an array via useMemo. This avoids the infinite re-render trap where
 * Object.values() inside a useShallow selector creates a new array reference
 * on every call, causing useShallow to always report a change.
 */
export const useAllMarketStats = (): {
  stats: WsMarketStatData[];
  isLoading: boolean;
  isConnected: boolean;
} => {
  const statsRecord = useMarketStatsStore((state) => state.stats);
  const isInitialized = useMarketStatsStore((state) => state.isInitialized);
  const isConnected = useMarketStatsStore((state) => state.isConnected);

  // Convert Record to array only when the store reference changes
  const stats = useMemo(() => Object.values(statsRecord), [statsRecord]);

  return { stats, isLoading: !isInitialized, isConnected };
};
